import { useState, useCallback, useEffect } from 'react'
import { useToken } from 'app/hooks'
import API from 'utils/api'
import * as antd from 'antd'

export interface MerchantStatus {
	memberId?: string
	merchantId?: string
	reviewStatus?: 'W' | 'P' | 'R' | 'S' | 'O' | 'N'
	openCode?: '0000' | '1010' | '1700'
	lastSyncedAt?: string
	isLinked?: boolean
	paymentAvailable?: boolean
	statusSource?: string
	subMerchantSyncErrorCode?: string
	subMerchantSyncErrorMessage?: string
}

export interface SubMerchantItem {
	memberId?: string
	merchantId?: string
	companyName?: string
	branchName?: string
	ceoName?: string
	email?: string
	businessNumber?: string
}

export function usePaySSAMMerchant(deptId?: number) {
	const { data: token } = useToken()
	const [merchantStatus, setMerchantStatus] = useState<MerchantStatus | null>(null)
	const [merchantInfo, setMerchantInfo] = useState<any>(null)
	const [subMerchants, setSubMerchants] = useState<SubMerchantItem[]>([])
	const [loadingStatus, setLoadingStatus] = useState(false)
	const [loadingInfo, setLoadingInfo] = useState(false)
	const [loadingSubMerchants, setLoadingSubMerchants] = useState(false)
	const [registering, setRegistering] = useState(false)
	const [statusChecked, setStatusChecked] = useState(false)

	useEffect(() => {
		setMerchantStatus(null)
		setMerchantInfo(null)
		setSubMerchants([])
		setStatusChecked(false)
	}, [deptId])

	const registerMerchant = useCallback(async () => {
		if (!deptId || !token) {
			throw new Error('지점 정보를 찾을 수 없습니다.')
		}

		setRegistering(true)
		try {
			const res = await API.post(`/payments/payssam/dept/${deptId}/merchant/register`, token + '', {})
			const payload = res.result?.result ?? res.result
			const url = payload?.info?.url
			if (res.result?.success !== false && url) {
				window.open(url, '_blank')
				antd.message.success('연동 페이지가 열렸습니다. 완료 후 상태를 새로고침해주세요.')
				return payload
			}
			throw new Error(res.result?.message || (payload?.msg) || '연동 시작에 실패했습니다.')
		} catch (err: any) {
			const errorMsg = err.message || '연동 시작 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setRegistering(false)
		}
	}, [deptId, token])

	const refreshStatus = useCallback(async (showMessage: boolean = true) => {
		if (!deptId || !token) return

		setLoadingStatus(true)
		try {
			const res = await API.get(`/payments/payssam/dept/${deptId}/merchant/status`, token + '', {})
			if (res.result?.success) {
				setMerchantStatus(res.result.result)
				if (showMessage) {
					antd.message.success('상태를 새로고침했습니다.')
				}
				return res.result.result
			} else {
				throw new Error(res.result?.message || '상태 조회에 실패했습니다.')
			}
		} catch (err: any) {
			if (showMessage) {
				const errorMsg = err.message || '상태 조회 중 오류가 발생했습니다.'
				antd.message.error(errorMsg)
			}
			console.warn(err)
		} finally {
			setLoadingStatus(false)
			setStatusChecked(true)
		}
	}, [deptId, token])

	const getMerchantInfo = useCallback(async () => {
		if (!deptId || !token) {
			throw new Error('지점 정보를 찾을 수 없습니다.')
		}

		setLoadingInfo(true)
		try {
			const res = await API.get(`/payments/payssam/dept/${deptId}/merchant/info`, token + '', {})
			if (res.result?.success && res.result?.result?.source !== 'db') {
				setMerchantInfo(res.result.result)
				return res.result.result
			}

			const subMerchantRes = await API.get('/payments/payssam/merchant/sub-merchants', token + '', {})
			if (subMerchantRes.result?.success) {
				const list = Array.isArray(subMerchantRes.result?.result?.data) ? subMerchantRes.result.result.data : []
				const matchedMerchant =
					list.find((item: SubMerchantItem) =>
						item.memberId === merchantStatus?.memberId && item.merchantId === merchantStatus?.merchantId
					) ||
					list.find((item: SubMerchantItem) => item.merchantId === merchantStatus?.merchantId) ||
					list.find((item: SubMerchantItem) => item.memberId === merchantStatus?.memberId) ||
					(list.length === 1 ? list[0] : null)

				if (matchedMerchant) {
					const normalized = {
						code: subMerchantRes.result?.result?.code,
						msg: subMerchantRes.result?.result?.msg,
						info: {
							member: matchedMerchant.memberId,
							merchant: matchedMerchant.merchantId,
							company: matchedMerchant.companyName,
							branch: matchedMerchant.branchName,
							ceo: matchedMerchant.ceoName,
							email: matchedMerchant.email,
							business_number: matchedMerchant.businessNumber,
						},
						source: 'api-v2',
					}
					setMerchantInfo(normalized)
					return normalized
				}
			}

			if (res.result?.success) {
				setMerchantInfo(res.result.result)
				return res.result.result
			}
			const payMsg = res.result?.result?.msg
			throw new Error(payMsg || res.result?.message || '가맹점 정보 조회에 실패했습니다.')
		} catch (err: any) {
			const errorMsg = err.message || '가맹점 정보 조회 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setLoadingInfo(false)
		}
	}, [deptId, token, merchantStatus?.memberId, merchantStatus?.merchantId])

	const getSubMerchants = useCallback(async () => {
		if (!token) {
			throw new Error('로그인 정보가 없습니다.')
		}

		setLoadingSubMerchants(true)
		try {
			const res = await API.get('/payments/payssam/merchant/sub-merchants', token + '', {})
			if (res.result?.success) {
				const list = Array.isArray(res.result?.result?.data) ? res.result.result.data : []
				setSubMerchants(list)
				return res.result.result
			}
			const payMsg = res.result?.result?.msg
			throw new Error(payMsg || res.result?.message || '하위 사업장 조회에 실패했습니다.')
		} catch (err: any) {
			const errorMsg = err.message || '하위 사업장 조회 중 오류가 발생했습니다.'
			antd.message.error(errorMsg)
			console.error(err)
			throw err
		} finally {
			setLoadingSubMerchants(false)
		}
	}, [token])

	return {
		merchantStatus,
		merchantInfo,
		subMerchants,
		loadingStatus,
		loadingInfo,
		loadingSubMerchants,
		registering,
		statusChecked,
		registerMerchant,
		refreshStatus,
		getMerchantInfo,
		getSubMerchants,
	}
}
